<script setup>
import Header from "@/components/Header.vue";
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import heroImage from "@/assets/img/background/image3.png";
import { loadSlim } from "tsparticles-slim";
import { getTiposBoleto } from "@/utils/api_evento";
import { useCompraBoletosStore } from "@/store/CompraBoletosStore";

const boletos = ref([]);
const cargando = ref(true);
const store = useCompraBoletosStore();
const router = useRouter();

const comprar = (id, nombre) => {
  store.boleto.idBoleto = id;
  store.boleto.nombreBoleto = nombre;
  router.push({ path: "/comprar-boletos" });
};

onMounted(async () => {
  try {
    boletos.value = await getTiposBoleto();
  } catch (error) {
    console.error("Error cargando boletos:", error);
  } finally {
    cargando.value = false;
  }
});

const formatPrecio = (valor) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(valor);

const accentTipo = (tipo) =>
  ({
    general: "#00aaff",
    primera_fila: "#00ccaa",
    mesa_vip: "#ffaa00",
    streaming: "#aa55ff",
  }[tipo] ?? "#00aaff");

const iconoTipo = (tipo) =>
  ({
    general: "bi-ticket-perforated",
    primera_fila: "bi-star",
    mesa_vip: "bi-gem",
    streaming: "bi-play-circle",
  }[tipo] ?? "bi-ticket");

const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 40 },
    color: { value: "#00aaff" },
    opacity: { value: 0.25 },
    size: { value: 3 },
    move: { enable: true, speed: 1.2, direction: "none", outModes: "out" },
  },
};
const particlesInit = async (engine) => await loadSlim(engine);
</script>

<template>
  <section id="boletos" class="boletos-wrapper">
    <div class="hero-bg-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="hero-gradient"></div>
    <Particles id="boletosParticles" :init="particlesInit" :options="particleConfig" />
    <div class="smoke-layer"></div>

    <div class="contenido">
      <Header />

      <div class="seccion" data-aos="fade-up">
        <div class="seccion-header">
          <span class="seccion-linea"></span>
          <h2 class="seccion-titulo">Elige tu <em>Boleto</em></h2>
          <span class="seccion-linea"></span>
        </div>

        <div v-if="cargando" class="estado-centro">
          <div class="spinner">
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>

        <div v-else-if="boletos.length" class="precios-grid">
          <div
            v-for="boleto in boletos"
            :key="boleto.id"
            class="card-precio"
            :class="{ 'card-precio--destacado': boleto.destacado }"
            :style="{ '--accent': accentTipo(boleto.tipo) }"
            @click="comprar(boleto.id, boleto.nombre)"
          >
            <div v-if="boleto.destacado" class="badge-top">⭐ Recomendado</div>
            <div class="card-precio__glow"></div>

            <!-- Listón diagonal rojo — hardcodeado para pruebas visuales -->
            <!-- TODO: reemplazar por boleto.ribbon_activo && boleto.ribbon_texto cuando el back esté listo -->
            <div v-if="boleto.ribbon_activo" class="ribbon-diagonal">
              {{ boleto.ribbon_texto || "Oferta Especial" }}
            </div>

            <div class="card-precio__header">
              <i :class="['bi', iconoTipo(boleto.tipo), 'card-icono']"></i>
              <span class="badge-modalidad">{{ boleto.nombre }}</span>
            </div>

            <p class="card-precio__monto">{{ formatPrecio(boleto.precio) }}</p>

            <p class="card-precio__desc">
              {{ boleto.mensaje_promocional || boleto.descripcion }}
            </p>

            <div class="card-precio__line"></div>

            <ul v-if="boleto.beneficios?.length" class="beneficios-lista">
              <li v-for="(b, i) in boleto.beneficios" :key="i" class="beneficio-item">
                <span class="beneficio-check">✓</span>
                {{ b }}
              </li>
            </ul>
          </div>
        </div>

        <p v-else class="vacio-msg">Boletos no disponibles aún.</p>

        <div v-if="boletos.length" class="cta-wrap" data-aos="fade-up">
          <a href="/comprar-boletos" class="cta-btn">
            <span class="cta-text">Comprar mi boleto</span>
            <span class="cta-arrow">→</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.boletos-wrapper {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}
.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.3) contrast(1.1);
  z-index: 1;
}
.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, #000 0%, rgba(0, 20, 45, 0.8) 50%, #000 100%);
  z-index: 2;
}
#boletosParticles {
  position: absolute;
  inset: 0;
  z-index: 3;
}
.smoke-layer {
  position: absolute;
  inset: 0;
  background: url("https://c4.wallpaperflare.com/wallpaper/448/699/737/abstract-digital-art-3d-abstract-lines-wallpaper-preview.jpg");
  opacity: 0.15;
  mix-blend-mode: lighten;
  animation: smokeMove 20s infinite linear;
  z-index: 4;
}
@keyframes smokeMove {
  0% {
    transform: translateY(0) scale(1.1);
  }
  100% {
    transform: translateY(-10%) scale(1.15);
  }
}
.contenido {
  position: relative;
  z-index: 10;
}
.seccion {
  width: min(1200px, 92vw);
  margin: 0 auto;
  padding-top: 2rem;
}
.seccion-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 1.5rem;
}
.seccion-linea {
  flex: 1;
  max-width: 100px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 170, 255, 0.4));
}
.seccion-header .seccion-linea:last-child {
  background: linear-gradient(90deg, rgba(0, 170, 255, 0.4), transparent);
}
.seccion-titulo {
  font-family: "Oswald", "Impact", sans-serif;
  font-size: clamp(1.4rem, 3.5vw, 2rem);
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
  letter-spacing: 2px;
  white-space: nowrap;
  margin: 0;
}
.seccion-titulo em {
  font-style: normal;
  color: transparent;
  -webkit-text-stroke: 1.5px #00aaff;
}
.estado-centro {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}
.spinner {
  position: relative;
  width: 40px;
  height: 40px;
}
.spinner div {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: #00aaff;
  animation: giro 1s linear infinite;
}
.spinner div:nth-child(2) {
  inset: 7px;
  border-top-color: rgba(0, 170, 255, 0.5);
  animation-duration: 1.6s;
  animation-direction: reverse;
}
.spinner div:nth-child(3) {
  inset: 14px;
  border-top-color: rgba(0, 170, 255, 0.2);
  animation-duration: 2.2s;
}
@keyframes giro {
  to {
    transform: rotate(360deg);
  }
}

/* ── Cards ── */
.precios-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  align-items: stretch;
}
.card-precio {
  cursor: pointer;
  position: relative;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--accent, #00aaff);
  border-radius: 20px;
  padding: 36px 28px 28px;
  text-align: center;
  backdrop-filter: blur(12px);
  width: 240px;
  max-width: 280px;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
  /* altura uniforme solo en desktop */
  flex: 1 1 240px;
}
.card-precio:hover {
  transform: translateY(-6px);
  box-shadow: 0 0 32px color-mix(in srgb, var(--accent) 25%, transparent);
}
.card-precio--destacado {
  border-width: 2px;
  box-shadow: 0 0 24px color-mix(in srgb, var(--accent) 20%, transparent);
}
.badge-top {
  position: absolute;
  top: 12px;
  left: 12px;
  right: auto;
  font-size: 0.52rem;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--accent);
  padding: 3px 8px;
  border-radius: 20px;
}

/* ── Listón diagonal rojo ── */
.ribbon-diagonal {
  position: absolute;
  top: 30px;
  right: -50px;
  width: 210px;
  background: #e53935;
  color: #fff;
  font-size: 0.55rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  padding: 7px 0;
  transform: rotate(45deg);
  transform-origin: center;
  border-top: 1px solid rgba(255, 255, 255, 0.35);
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);
  pointer-events: none;
  z-index: 20;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
}
.card-precio__glow {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  width: 140px;
  height: 140px;
  background: radial-gradient(circle, var(--accent, #00aaff) 0%, transparent 70%);
  opacity: 0.1;
  pointer-events: none;
}
.card-precio__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.card-icono {
  font-size: 2rem;
  color: var(--accent);
  filter: drop-shadow(0 0 8px var(--accent));
}
.badge-modalidad {
  background: var(--accent, #00aaff);
  color: #000;
  font-size: 0.6rem;
  font-weight: 900;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
}
.card-precio__monto {
  font-family: "Oswald", sans-serif;
  font-size: 2.4rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 6px;
}
.card-precio__desc {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
  letter-spacing: 0.5px;
  line-height: 1.5;
  min-height: 2rem;
}
.card-precio__line {
  height: 1px;
  margin: 18px 0 16px;
  background: linear-gradient(90deg, transparent, var(--accent, #00aaff), transparent);
  opacity: 0.35;
}

/* ── Beneficios ── */
.beneficios-lista {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* sin flex:1 — el contenido define la altura, visible en móvil y desktop */
}
.beneficio-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.4;
}
.beneficio-check {
  color: var(--accent);
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
  margin-top: 1px;
}
.vacio-msg {
  text-align: center;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.9rem;
  padding: 2rem 0;
}

/* ── CTA ── */
.cta-wrap {
  display: flex;
  justify-content: center;
  padding-top: 1.5rem;
}
.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  padding: 16px 52px;
  border-radius: 6px;
  color: #fff;
  text-decoration: none;
  font-family: "Oswald", sans-serif;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  border: 1px solid #00aaff;
  background: linear-gradient(120deg, #0066cc, #00aaff);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: pulseGlow 3s ease-in-out infinite;
  cursor: pointer;
}
.cta-btn::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, #0088ee, #33ccff);
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: 0;
}
.cta-btn:hover::before {
  opacity: 1;
}
.cta-btn:active {
  transform: scale(0.97);
}
.cta-text,
.cta-arrow {
  position: relative;
  z-index: 1;
}
.cta-arrow {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}
.cta-btn:hover .cta-arrow {
  transform: translateX(5px);
}
@keyframes pulseGlow {
  0%,
  100% {
    box-shadow: 0 0 0 rgba(0, 170, 255, 0);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 170, 255, 0.2);
  }
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .precios-grid {
    gap: 16px;
  }
  .card-precio {
    width: 180px;
    padding: 28px 20px 24px;
  }
  .card-precio__monto {
    font-size: 2rem;
  }
  .cta-btn {
    padding: 14px 32px;
    white-space: nowrap;
    font-size: 0.9rem;
  }
}
@media (max-width: 480px) {
  .precios-grid {
    flex-direction: column;
    align-items: center;
  }
  .card-precio {
    width: min(300px, 90vw);
    /* en móvil no forzamos altura uniforme */
    flex: none;
  }
  .seccion-titulo {
    font-size: 1.2rem;
  }
  .cta-wrap {
    padding-left: 16px;
    padding-right: 16px;
  }
  .cta-btn {
    width: 100%;
    justify-content: center;
    padding: 14px 24px;
    white-space: nowrap;
    font-size: 0.85rem;
    letter-spacing: 1.5px;
  }
}
</style>
